import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { isNativeApp, shareTextFile } from "../../lib/nativeFile";
import { BookOpen, ChevronDown, ChevronRight, Download, LogIn, Plus, RefreshCw, Search, Trash2, UserPlus } from "lucide-react";

function formatDate(date) {
  if (!date) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(new Date(date));
  } catch {
    return "—";
  }
}

function formatDateTime(date) {
  if (!date) return "—";
  try {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(date));
  } catch {
    return "—";
  }
}

function relativeDate(date) {
  if (!date) return "jamais";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return "aujourd’hui";
  if (diffDays === 1) return "hier";
  if (diffDays < 30) return `il y a ${diffDays} jours`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `il y a ${diffMonths} mois`;
  const diffYears = Math.floor(diffMonths / 12);
  return `il y a ${diffYears} an${diffYears > 1 ? "s" : ""}`;
}

function csvEscape(value) {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", course_ids: [] });
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [savingInvite, setSavingInvite] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [grantSelections, setGrantSelections] = useState({});
  const [grantingId, setGrantingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [studentsRes, coursesRes] = await Promise.all([
        api.get("/admin/students"),
        api.get("/courses/admin/all"),
      ]);
      setStudents(studentsRes.data || []);
      setCourses(coursesRes.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de charger les apprenants.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredStudents = useMemo(() => {
    const q = query.trim().toLowerCase();
    const data = students.filter((student) => {
      if (!q) return true;
      const searchable = [
        student.name,
        student.email,
        ...(student.courses || []).map((course) => course.title),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchable.includes(q);
    });

    return [...data].sort((a, b) => {
      if (sortBy === "name_asc") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "progress_desc") {
        const avgA = averageProgress(a);
        const avgB = averageProgress(b);
        return avgB - avgA;
      }
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return db - da;
    });
  }, [students, query, sortBy]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSavingInvite(true);
    setError("");
    setTemporaryPassword("");

    try {
      const payload = {
        name: invite.name,
        email: invite.email,
        password: invite.password || undefined,
        course_ids: invite.course_ids || [],
      };
      const res = await api.post("/admin/students", payload);
      if (res.data?.temporary_password) {
        setTemporaryPassword(res.data.temporary_password);
      }
      setInvite({ name: "", email: "", password: "", course_ids: [] });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’ajouter cet apprenant.");
    } finally {
      setSavingInvite(false);
    }
  };

  const handleDelete = async (student) => {
    const confirmed = window.confirm(
      `Supprimer l’apprenant ${student.name || student.email} ?\n\nCette action supprimera son compte, ses accès formations, sa progression, ses devoirs et ses données associées.`
    );
    if (!confirmed) return;

    setDeletingId(student.user_id);
    setError("");
    try {
      await api.delete(`/admin/students/${student.user_id}`);
      setStudents((prev) => prev.filter((item) => item.user_id !== student.user_id));
      window.location.reload();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de supprimer cet apprenant.");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleInviteCourse = (courseId) => {
    setInvite((prev) => {
      const current = prev.course_ids || [];
      return {
        ...prev,
        course_ids: current.includes(courseId)
          ? current.filter((id) => id !== courseId)
          : [...current, courseId],
      };
    });
  };

  const toggleGrantCourse = (userId, courseId) => {
    setGrantSelections((prev) => {
      const current = prev[userId] || [];
      return {
        ...prev,
        [userId]: current.includes(courseId)
          ? current.filter((id) => id !== courseId)
          : [...current, courseId],
      };
    });
  };

  const grantSelectedCourses = async (student) => {
    const selected = grantSelections[student.user_id] || [];
    if (!selected.length) return;
    setGrantingId(student.user_id);
    setError("");
    try {
      await api.post(`/admin/students/${student.user_id}/courses`, { course_ids: selected });
      setGrantSelections((prev) => ({ ...prev, [student.user_id]: [] }));
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d’attribuer ces formations.");
    } finally {
      setGrantingId(null);
    }
  };

  const toggleExpanded = (userId) => {
    setExpanded((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const exportCsv = async () => {
    const rows = [
      ["Nom", "Email", "Formations", "Progression moyenne", "Revenus", "Inscrit le", "Connexions", "Première connexion", "Dernière connexion", "Dernière activité"],
      ...filteredStudents.map((student) => [
        student.name || "",
        student.email || "",
        (student.courses || []).map((course) => course.title).join(" | "),
        `${averageProgress(student)}%`,
        `${student.revenue_eur || 0}€`,
        formatDate(student.created_at),
        student.login_count || 0,
        formatDateTime(student.first_login_at),
        formatDateTime(student.last_login_at),
        formatDateTime(student.last_activity_at),
      ]),
    ];
    const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\n");
    const filename = "apprenants-sf-learning.csv";

    // Dans l'app mobile, le telechargement web ne produit aucun fichier : on
    // passe par le partage natif (enregistrer, envoyer par mail, ouvrir...).
    if (isNativeApp()) {
      try {
        const shared = await shareTextFile(filename, csv, "Apprenants SF Learning");
        if (shared) return;
      } catch (e) {
        // partage annule ou indisponible : on retombe sur le telechargement web
      }
    }

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-students-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-6">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-4xl font-black">Apprenants</h1>
        </div>
        <button type="button" onClick={exportCsv} className="btn-primary bg-cyan-500 hover:bg-cyan-600">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px_220px] gap-4 mb-6">
        <label className="block">
          <span className="block text-sm text-gray-700 mb-1">Filtrer par</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtrez par email, prénom, nom ou formation"
              className="input pl-10"
            />
          </div>
        </label>
        <label className="block">
          <span className="block text-sm text-gray-700 mb-1">Type</span>
          <select className="input" value="registered" disabled>
            <option value="registered">Inscrits</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-sm text-gray-700 mb-1">Trier par</span>
          <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created_desc">Date d’inscription</option>
            <option value="name_asc">Nom</option>
            <option value="progress_desc">Progression</option>
          </select>
        </label>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
        <p className="text-sm">
          Résultat : <strong>{filteredStudents.length} apprenant{filteredStudents.length > 1 ? "s" : ""}</strong>
        </p>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowInvite((v) => !v)} className="btn-primary bg-cyan-500 hover:bg-cyan-600">
            <UserPlus className="w-4 h-4" /> Inviter un apprenant
          </button>
          <button type="button" onClick={load} className="btn-ghost" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </button>
        </div>
      </div>

      {showInvite && (
        <form onSubmit={handleInvite} className="card p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label mb-1 block">Nom</label>
            <input className="input" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} required />
          </div>
          <div>
            <label className="label mb-1 block">Email</label>
            <input className="input" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} required />
          </div>
          <div>
            <label className="label mb-1 block">Mot de passe provisoire</label>
            <input className="input" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} placeholder="Laisser vide pour générer automatiquement" />
          </div>
          <div className="md:col-span-2">
            <label className="label mb-2 block">Formations à attribuer</label>
            <p className="text-xs text-gray-500 mb-2">Sélectionne une ou plusieurs formations à placer manuellement sur cet apprenant.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto rounded-xl border border-emerald-100 bg-emerald-50/50 p-2">
              {courses.length === 0 ? (
                <p className="text-sm text-gray-500 p-3">Aucune formation disponible.</p>
              ) : courses.map((course) => {
                const selected = (invite.course_ids || []).includes(course.course_id);
                return (
                  <label key={course.course_id} className={`flex items-center gap-3 rounded-lg p-3 cursor-pointer transition ${selected ? "bg-brand-green text-white" : "bg-white hover:bg-emerald-100"}`}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleInviteCourse(course.course_id)}
                      className="h-4 w-4"
                    />
                    <span className="text-sm font-semibold">{course.title}</span>
                  </label>
                );
              })}
            </div>
          </div>
          {temporaryPassword && (
            <div className="md:col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Mot de passe provisoire généré : <strong>{temporaryPassword}</strong>
            </div>
          )}
          <div className="md:col-span-2 flex justify-end gap-3">
            <button type="button" className="btn-ghost" onClick={() => setShowInvite(false)}>Annuler</button>
            <button type="submit" className="btn-primary" disabled={savingInvite}>
              <Plus className="w-4 h-4" /> {savingInvite ? "Ajout..." : "Ajouter l’apprenant"}
            </button>
          </div>
        </form>
      )}

      <div className="card overflow-hidden">
        <div className="hidden md:grid grid-cols-[40px_1.5fr_1fr_1fr_140px] gap-4 bg-gray-50 px-5 py-4 text-sm font-bold text-gray-700 border-b border-gray-200">
          <div />
          <div>Apprenant</div>
          <div className="text-center">Activité</div>
          <div className="text-center">Progression</div>
          <div className="text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Chargement des apprenants...</div>
        ) : filteredStudents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucun apprenant trouvé.</div>
        ) : (
          filteredStudents.map((student) => (
            <div key={student.user_id} className="border-b border-gray-100 last:border-b-0" data-testid={`student-row-${student.user_id}`}>
              <div className="grid grid-cols-1 md:grid-cols-[40px_1.5fr_1fr_1fr_140px] gap-4 items-center px-5 py-5">
                <button type="button" onClick={() => toggleExpanded(student.user_id)} className="hidden md:flex text-brand-green">
                  {expanded[student.user_id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                </button>

                <div>
                  <p className="font-medium">{student.name || "Sans nom"}</p>
                  <p className="font-bold text-sm">{student.email}</p>
                </div>

                <div className="text-sm text-center text-gray-700">
                  <p><strong>Inscrit</strong> {relativeDate(student.created_at)}</p>
                  <p>
                    <strong>{student.login_count || 0} connexion{(student.login_count || 0) > 1 ? "s" : ""}</strong>
                    {student.last_login_at ? ` · ${relativeDate(student.last_login_at)}` : ""}
                  </p>
                  <p className="text-gray-500"><strong>Activité</strong> {relativeDate(student.last_activity_at)}</p>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span>{student.courses_count || 0} formation{student.courses_count > 1 ? "s" : ""}</span>
                    <span>{averageProgress(student)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-brand-green" style={{ width: `${averageProgress(student)}%` }} />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <button type="button" onClick={() => toggleExpanded(student.user_id)} className="btn-ghost !px-3 md:hidden">
                    Détail
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(student)}
                    disabled={deletingId === student.user_id}
                    className="inline-flex items-center justify-center rounded-lg p-2 text-red-500 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Supprimer cet apprenant"
                    aria-label={`Supprimer ${student.name || student.email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {expanded[student.user_id] && (
                <div className="mx-5 mb-5 rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-4">
                  <div className="rounded-xl border border-emerald-100 bg-white p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
                      <div>
                        <h3 className="font-heading font-bold flex items-center gap-2"><BookOpen className="w-4 h-4 text-brand-green" /> Ajouter des accès formations</h3>
                        <p className="text-sm text-gray-500">Utile si tu as encaissé hors plateforme ou via un lien Stripe manuel.</p>
                      </div>
                      <button
                        type="button"
                        className="btn-primary !px-4"
                        disabled={grantingId === student.user_id || !(grantSelections[student.user_id] || []).length}
                        onClick={() => grantSelectedCourses(student)}
                      >
                        {grantingId === student.user_id ? "Attribution..." : "Attribuer"}
                      </button>
                    </div>
                    {(() => {
                      const ownedCourseIds = new Set((student.courses || []).map((course) => course.course_id));
                      const availableCourses = courses.filter((course) => !ownedCourseIds.has(course.course_id));
                      if (!availableCourses.length) {
                        return <p className="text-sm text-gray-500">Toutes les formations disponibles sont déjà attribuées à cet apprenant.</p>;
                      }
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {availableCourses.map((course) => {
                            const selected = (grantSelections[student.user_id] || []).includes(course.course_id);
                            return (
                              <label key={course.course_id} className={`flex items-center gap-3 rounded-lg p-3 cursor-pointer transition ${selected ? "bg-brand-green text-white" : "bg-emerald-50 hover:bg-emerald-100"}`}>
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={() => toggleGrantCourse(student.user_id, course.course_id)}
                                  className="h-4 w-4"
                                />
                                <span className="text-sm font-semibold">{course.title}</span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  <LoginHistory userId={student.user_id} />

                  {(student.courses || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Aucune formation attribuée.</p>
                  ) : (
                    student.courses.map((course) => (
                      <div key={course.course_id} className="rounded-xl bg-white p-4 border border-gray-100">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
                          <div>
                            <h3 className="font-heading font-bold text-lg">{course.title}</h3>
                            <p className="text-sm text-gray-500">Commencée {relativeDate(course.enrolled_at)}</p>
                          </div>
                          <div className="flex flex-wrap gap-2 text-sm">
                            <span className="rounded-full bg-white border px-3 py-1">{course.completed_lessons} / {course.total_lessons} leçons ({course.progress_pct}%)</span>
                            <span className="rounded-full bg-white border px-3 py-1">Achetée {(student.revenue_eur || 0).toFixed(2)} €</span>
                          </div>
                        </div>
                        <div className="h-3 rounded-full bg-gray-100 overflow-hidden mb-3">
                          <div className="h-full bg-brand-green" style={{ width: `${course.progress_pct}%` }} />
                        </div>
                        <p className="text-sm text-right text-gray-600">
                          <strong>Dernière leçon vue :</strong> {course.last_lesson_title || "aucune"} {course.last_activity_at ? `· ${relativeDate(course.last_activity_at)}` : ""}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function averageProgress(student) {
  const courses = student.courses || [];
  if (!courses.length) return 0;
  return Math.round(courses.reduce((sum, course) => sum + (course.progress_pct || 0), 0) / courses.length);
}

function LoginHistory({ userId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setData(null);
    setError(false);
    api
      .get(`/admin/students/${userId}/logins`, { params: { limit: 200 } })
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [userId]);

  return (
    <div className="rounded-xl bg-white p-4 border border-gray-100">
      <h3 className="font-heading font-bold flex items-center gap-2 mb-1">
        <LogIn className="w-4 h-4 text-brand-green" /> Connexions{data ? ` (${data.total || 0})` : ""}
      </h3>
      <p className="text-sm text-gray-500 mb-3">Preuve d’accès datée (utile pour la garantie remboursement).</p>
      {error ? (
        <p className="text-sm text-gray-500">Historique de connexions indisponible.</p>
      ) : !data ? (
        <p className="text-sm text-gray-500">Chargement des connexions…</p>
      ) : (data.logins || []).length === 0 ? (
        <p className="text-sm text-gray-500">Aucune connexion enregistrée pour l’instant.</p>
      ) : (
        <ul className="text-sm text-gray-700 divide-y divide-gray-100 max-h-64 overflow-auto">
          {(data.logins || []).map((l, i) => (
            <li key={i} className="flex items-center justify-between gap-3 py-1.5">
              <span>{formatDateTime(l.created_at)}</span>
              <span className="text-xs text-gray-500 truncate">
                {l.method || "—"}{l.ip_hint ? ` · ${l.ip_hint}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
